import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IListUsersService } from "@modules/user/models/IListUsersService";
import { IUser } from "@utils/interfaces";
import ROUTES from "@config/routes";

export function useFetchUser(id: string, userFromState?: IUser | null) {
  const [user, setUser] = useState<IUser | null>(userFromState ?? null);
  const [isLoading, setIsLoading] = useState(!userFromState);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const iocContext = useIoCContext();
  const hasFetchedRef = useRef(false);

  const listUsersService = iocContext.serviceContainer.get<IListUsersService>(
    Types.Users.IListUsersService
  );

  useEffect(() => {
    if (userFromState) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let cancelled = false;

    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const users = await listUsersService.execute();
        const found = users.find((u) => u.id === id) ?? null;

        if (cancelled) return;

        if (!found) {
          enqueueSnackbar("Usuário não encontrado", { variant: "error" });
          history.push(ROUTES.ADMIN_ROUTES.LIST_USERS);
          return;
        }

        setUser(found);
      } catch {
        if (cancelled) return;
        enqueueSnackbar("Erro ao carregar dados do usuário", {
          variant: "error",
        });
        history.push(ROUTES.ADMIN_ROUTES.LIST_USERS);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchUser();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userFromState]);

  return { user, isLoading };
}
